import React, { useMemo, useState } from "react";
import dayjs from "dayjs";
import { MdUnfoldMore } from "react-icons/md";

// Every other app feature, per professional, counted from the tables the
// actions write to (getEmployeeOpsReport.featureUsage) - so it includes
// history, unlike the Professional App log which starts at deploy.

const rupees = (v) => (v ? `₹${Math.round(v).toLocaleString("en-IN")}` : "—");
const n = (v) => (v ? v.toLocaleString("en-IN") : <span className="text-gray-300">0</span>);
const daysAgo = (d) => (d ? dayjs().startOf("day").diff(dayjs(d), "day") : null);

// Team totals, grouped the way the app's menus are.
const TILES = [
  {
    title: "Booking outcomes",
    items: (s) => [
      ["Declined", s.declined, "", "outcomes"],
      ["Cancelled by pro (OTP)", s.cancelledByPro, "", "outcomes"],
      ["Stopped midway", s.stoppedMidway, `${s.stoppedConfirmed || 0} confirmed by customer`, "outcomes"],
      ["Issues reported", s.issuesReported, `${s.issuesResolved || 0} resolved`, "issues"],
      ["Reschedule allowed", s.rescheduleAllowed, "", "outcomes"],
    ],
  },
  {
    title: "During the service",
    items: (s) => [
      ["Bookings with add-ons", s.addOnBookings, `${s.addOnLines || 0} services · ${s.addOnViaOtp || 0} via customer OTP`, "addons"],
      ["Customer rated by pro", s.ratedCustomer, s.ratableBookings ? `of ${s.ratableBookings} completed` : "", "rating"],
    ],
  },
  {
    title: "Payment & cash",
    items: (s) => [
      ["Cash collected", rupees(s.cashCollected), `${s.cashBookings || 0} bookings`, "payments"],
      ["Paid by QR", s.qrPaid, "completed bookings", "payments"],
      ["Cash not settled", rupees(s.cashPending), `${s.cashPendingBookings || 0} bookings · ${s.settleRequested || 0} asked to settle · all time`, "cash"],
    ],
  },
  {
    title: "Attendance & leave",
    items: (s) => [
      ["Days present", s.daysPresent, `${s.selfMarked || 0} marked in the app`, "attendance"],
      ["Leave applied", s.leaveApplied, `${s.leaveApproved || 0} approved · ${s.leaveRejected || 0} rejected · ${s.leaveCancelled || 0} withdrawn`, "leave"],
      ["Marked absent", s.markedAbsent, "no leave applied", "leave"],
    ],
  },
  {
    title: "Products & stock",
    items: (s) => [
      ["Stock requests", s.stockRequests, `${s.stockFulfilled || 0} fulfilled · ${s.stockCancelled || 0} cancelled`, "stockRequests"],
      ["Stock accepted", s.stockAccepted, `${s.stockRejected || 0} rejected`, "stockMoves"],
      ["Written off (lost)", s.stockWrittenOff, "", "stockMoves"],
      ["Weekly audits", s.auditCycles, `${s.auditsMissed || 0} missed · ${s.auditItemsFlagged || 0} items flagged`],
    ],
  },
  {
    title: "Pay, rewards & app",
    items: (s) => [
      ["Deduction disputes", s.disputesRaised, `${s.disputesWaived || 0} waived · ${s.disputesOpen || 0} open`, "disputes"],
      ["Coins redeemed", s.coinsRedeemed, `${s.redemptions || 0} times · ${s.coinsEarned || 0} earned`, "rewards"],
      ["App errors", s.appErrors, "error reports from the app", "appErrors"],
    ],
  },
];

// Per-professional columns: [key, label, title, cell, sortValue].
const COLUMNS = [
  ["declined", "Declined", "Bookings they declined", (e) => n(e.declined)],
  ["cancelledByPro", "Cancelled", "Cancelled by the professional with the customer's OTP", (e) => n(e.cancelledByPro)],
  ["stoppedMidway", "Stopped", "Service stopped midway (partial completion)", (e) => n(e.stoppedMidway)],
  ["issuesReported", "Issues", "Issues reported from the app", (e) => n(e.issuesReported)],
  ["addOnBookings", "Add-ons", "Bookings where they added a service", (e) => n(e.addOnBookings)],
  [
    "ratedCustomer",
    "Rated cust.",
    "Completed bookings where they rated the customer",
    (e) => (e.ratableBookings ? `${e.ratedCustomer || 0}/${e.ratableBookings}` : <span className="text-gray-300">—</span>),
    (e) => (e.ratableBookings ? (e.ratedCustomer || 0) / e.ratableBookings : -1),
  ],
  ["cashCollected", "Cash coll.", "Cash collected on completed bookings", (e) => rupees(e.cashCollected)],
  ["qrPaid", "QR paid", "Completed bookings paid by the app's QR", (e) => n(e.qrPaid)],
  [
    "cashPending",
    "Unsettled",
    "Cash still with them, all time (oldest booking in days)",
    (e) => {
      if (!e.cashPending) return <span className="text-gray-300">—</span>;
      const d = daysAgo(e.oldestUnsettled);
      return (
        <span className="text-amber-700 font-medium">
          {rupees(e.cashPending)} <span className={`text-xs ${d > 3 ? "text-red-600" : "text-gray-400"}`}>{d}d</span>
        </span>
      );
    },
  ],
  ["settleRequested", "Asked", "Bookings they asked the office to settle", (e) => n(e.settleRequested)],
  ["daysPresent", "Present", "Days marked present (in the app or by the office)", (e) => (e.daysPresent ? `${e.daysPresent}${e.selfMarked !== e.daysPresent ? ` (${e.selfMarked || 0} self)` : ""}` : n(0))],
  ["leaveApplied", "Leave", "Leave requests applied", (e) => n(e.leaveApplied)],
  ["markedAbsent", "Absent", "Marked absent without leave", (e) => n(e.markedAbsent)],
  ["stockRequests", "Stock req.", "Stock requests raised", (e) => n(e.stockRequests)],
  ["stockAccepted", "Accepted", "Stock items accepted in the app (rejected in brackets)", (e) => (e.stockAccepted || e.stockRejected ? `${e.stockAccepted || 0}${e.stockRejected ? ` (${e.stockRejected}✕)` : ""}` : n(0))],
  ["stockWrittenOff", "Lost", "Items they reported lost", (e) => n(e.stockWrittenOff)],
  [
    "auditCycles",
    "Audits",
    "Weekly audit cycles (missed in red)",
    (e) => (e.auditCycles ? (
      <span>
        {e.auditCycles - (e.auditsMissed || 0)}/{e.auditCycles}
        {e.auditsMissed ? <span className="text-red-600 text-xs"> {e.auditsMissed} missed</span> : null}
      </span>
    ) : <span className="text-gray-300">—</span>),
  ],
  ["disputesRaised", "Disputes", "Deduction disputes raised", (e) => n(e.disputesRaised)],
  ["coinsRedeemed", "Coins out", "Reward coins redeemed", (e) => n(e.coinsRedeemed)],
  ["rulebookSeenUpTo", "Rulebook", "When they last read the rulebook changes", (e) => (e.rulebookSeenUpTo ? dayjs(e.rulebookSeenUpTo).format("D MMM") : <span className="text-gray-300">never</span>), (e) => (e.rulebookSeenUpTo ? dayjs(e.rulebookSeenUpTo).valueOf() : 0)],
  ["appErrors", "Errors", "App error reports", (e) => (e.appErrors ? <span className="text-red-600">{e.appErrors}</span> : n(0))],
  ["appVersion", "App ver.", "App version on their last error report", (e) => e.appVersion || <span className="text-gray-300">—</span>, (e) => e.appVersion || ""],
];

// Backend section each column comes from (feature-usage.js SECTIONS).
const COLUMN_SECTION = {
  declined: "outcomes", cancelledByPro: "outcomes", stoppedMidway: "outcomes", issuesReported: "issues", addOnBookings: "addons",
  ratedCustomer: "rating", cashCollected: "payments", qrPaid: "payments", cashPending: "cash", settleRequested: "cash",
  daysPresent: "attendance", leaveApplied: "leave", markedAbsent: "leave", stockRequests: "stockRequests", stockAccepted: "stockMoves",
  stockWrittenOff: "stockMoves", auditCycles: "audits", disputesRaised: "disputes", coinsRedeemed: "rewards", rulebookSeenUpTo: "rulebook",
  appErrors: "appErrors", appVersion: "appVersion",
};

const FeatureUsage = ({ usage, onPick }) => {
  const [sort, setSort] = useState({ key: "cashPending", dir: -1 });
  const s = usage?.summary || {};
  const failed = (usage?.sections || []).filter((x) => !x.ok);
  const failedKeys = new Set(failed.map((x) => x.key));

  const rows = useMemo(() => {
    const list = (usage?.employees || []).slice();
    const col = COLUMNS.find((c) => c[0] === sort.key);
    const val = col && col[4] ? col[4] : (e) => e[sort.key] || 0;
    list.sort((a, b) => {
      const x = val(a);
      const y = val(b);
      if (typeof x === "string" || typeof y === "string") return String(x).localeCompare(String(y)) * sort.dir;
      return (x - y) * sort.dir;
    });
    return list;
  }, [usage, sort]);

  // Columns whose data didn't load are hidden rather than shown as zeros.
  const columns = COLUMNS.filter((c) => !failedKeys.has(COLUMN_SECTION[c[0]]));

  if (!usage) return null;
  const toggleSort = (key) => setSort((cur) => (cur.key === key ? { key, dir: -cur.dir } : { key, dir: -1 }));

  return (
    <section className="bg-white rounded-xl border border-gray-100 shadow-sm">
      <header className="px-4 pt-4">
        <h2 className="font-semibold text-gray-900">All other app features</h2>
        <p className="text-xs text-gray-500 mt-0.5">
          Counted from what each action saves, so older months are included. Bookings by booking date; attendance, leave, stock, disputes and coins by the day they happened. Cash not settled and rulebook are all time.
        </p>
      </header>
      <div className="p-4 space-y-5">
        {failed.length > 0 && (
          <p className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2">
            Couldn't load: {failed.map((f) => f.label).join(", ")}. The rest is complete.
          </p>
        )}

        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {TILES.map((tile) => (
            <div key={tile.title} className="rounded-lg border border-gray-100 p-3">
              <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">{tile.title}</p>
              <dl className="space-y-1.5 text-sm">
                {tile.items(s).map(([label, value, sub, section]) => (
                  <div key={label} className="flex items-baseline gap-2">
                    <dt className="text-gray-600 flex-1 min-w-0">
                      {label}
                      {sub && !failedKeys.has(section) && <span className="block text-[11px] text-gray-400 leading-tight">{sub}</span>}
                    </dt>
                    <dd className="font-semibold tabular-nums text-gray-900">
                      {failedKeys.has(section) ? <span className="text-gray-300 font-normal">n/a</span> : typeof value === "number" ? value.toLocaleString("en-IN") : value || 0}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>

        <div className="overflow-x-auto -mx-4">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                <th className="px-4 py-2 font-medium sticky left-0 bg-white">Professional</th>
                {columns.map(([key, label, title]) => (
                  <th key={key} title={title} className="px-3 py-2 font-medium whitespace-nowrap">
                    <button type="button" onClick={() => toggleSort(key)} className={`inline-flex items-center gap-0.5 hover:text-gray-900 ${sort.key === key ? "text-gray-900" : ""}`}>
                      {label}
                      {sort.key === key ? <span>{sort.dir < 0 ? "↓" : "↑"}</span> : <MdUnfoldMore className="text-gray-300" />}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.employeeId} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="px-4 py-2 sticky left-0 bg-white">
                    <button type="button" onClick={() => onPick && onPick(e.employeeId)} className="font-medium text-gray-900 hover:underline text-left whitespace-nowrap">
                      {e.employeeName || `#${e.employeeId}`}
                    </button>
                  </td>
                  {columns.map(([key, , , cell]) => (
                    <td key={key} className="px-3 py-2 tabular-nums whitespace-nowrap">
                      {cell(e)}
                    </td>
                  ))}
                </tr>
              ))}
              {!rows.length && (
                <tr>
                  <td colSpan={columns.length + 1} className="px-4 py-8 text-center text-gray-400">Nothing in this period.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-gray-400">
          Not saved anywhere before the Professional App log, so not counted here: SOS presses, logins and app opens, QR codes shown but not paid, "check payment" taps, timer cancels, and screens they only look at. See Professional App for those from its start date.
        </p>
      </div>
    </section>
  );
};

export default FeatureUsage;
